import { Seo } from '../components/Seo';
import Logo from '../components/Logo';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, Loader2, Eye, EyeOff, CheckCircle, Mail } from 'lucide-react';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { captureLogin } from '../lib/analytics';

const Login = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Check for success messages from verification flow
  const justVerified = searchParams.get('verified') === 'true';
  const verificationSent = searchParams.get('verification_sent') === 'true';

  // Google sign-in sends people back here if it could not complete
  useEffect(() => {
    const authError = searchParams.get('auth_error');
    if (!authError) return;
    setError(
      authError === 'access_denied' || authError === 'cancelled'
        ? 'Google sign-in was cancelled. You can try again or use your email address.'
        : authError === 'expired'
        ? 'That Google sign-in took too long. Please try again.'
        : 'We could not finish signing you in with Google. Please try again or use your email address.'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      captureLogin('email');
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout">
      <Seo
        title="Sign In | VerifAEye"
        description="Sign in to your VerifAEye account to manage your bionic home inventory, analyse new photos and generate an insurer-ready inventory with a supporting cover letter in minutes."
        path="/login"
      />
      {/* Image Panel */}
      <div 
        className="auth-image-panel"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=1000&q=80)'
        }}
      />
      
      {/* Form Panel */}
      <div className="auth-form-panel">
        <div className="w-full max-w-md animate-slide-up">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <Logo size="md" />
          </Link>

          <Card className="border-slate-200">
            <CardHeader className="space-y-1">
              <CardTitle 
                className="text-2xl font-semibold text-slate-900"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                Welcome back
              </CardTitle>
              <CardDescription className="text-slate-500">
                Sign in to your account to continue
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Success: Email just verified */}
                {justVerified && (
                  <div 
                    className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm"
                    data-testid="verification-success"
                  >
                    <CheckCircle className="w-4 h-4 flex-shrink-0" />
                    <span>Email verified! Sign in to access your 5 free AI analyses.</span>
                  </div>
                )}

                {/* Info: Verification email sent */}
                {verificationSent && !justVerified && (
                  <div 
                    className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm"
                    data-testid="verification-sent"
                  >
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <span>Verification email sent! Check your inbox, then sign in.</span>
                  </div>
                )}

                {error && (
                  <div 
                    className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
                    data-testid="login-error"
                  >
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                    className="border-slate-200 focus:border-slate-400"
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-slate-700">Password</Label>
                    <Link 
                      to="/forgot-password" 
                      className="text-xs text-blue-600 hover:text-blue-700"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="login-password-input"
                      className="border-slate-200 focus:border-slate-400 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-slate-900 text-white hover:bg-slate-800"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>

              <div className="mt-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="text-xs text-slate-400">or</span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
              <div className="mt-4">
                <GoogleSignInButton label="Sign in with Google" />
              </div>

              <div className="mt-6 text-center">
                <p className="text-slate-500 text-sm">
                  Don't have an account?{' '}
                  <Link 
                    to="/register" 
                    className="text-blue-600 hover:text-blue-700 font-medium"
                    data-testid="login-register-link"
                  >
                    Create account
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;
